import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    Alert,
    Chip,
} from '@mui/material';
import { IconMail, IconEye, IconDeviceFloppy, IconRefresh } from '@tabler/icons';
import MainCard from 'ui-component/cards/MainCard';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GET_CONFIRMATION_EMAIL_TEMPLATE = gql`
    query GetConfirmationEmailTemplate($eventId: String!, $role: ROLE!) {
        confirmationEmailTemplate(eventId: $eventId, role: $role) {
            subject
            body
            isHtml
        }
        invitationVariables(eventId: $eventId, role: $role)
    }
`;

const SAVE_CONFIRMATION_EMAIL_TEMPLATE = gql`
    mutation SaveConfirmationEmailTemplate(
        $eventId: String!
        $role: ROLE!
        $subject: String!
        $body: String!
        $isHtmlMode: Boolean!
    ) {
        saveConfirmationEmailTemplate(
            eventId: $eventId
            role: $role
            subject: $subject
            body: $body
            isHtmlMode: $isHtmlMode
        )
    }
`;

const SEND_TEST_CONFIRMATION_EMAIL = gql`
    mutation SendTestConfirmationEmail($eventId: String!, $role: ROLE!, $testEmail: String!) {
        sendTestConfirmationEmail(eventId: $eventId, role: $role, testEmail: $testEmail)
    }
`;

const PREVIEW_EMAIL_TEMPLATE = gql`
    mutation PreviewEmailTemplate(
        $eventId: String!
        $role: ROLE!
        $subject: String!
        $body: String!
        $templateType: String!
    ) {
        previewEmailTemplate(
            eventId: $eventId
            role: $role
            subject: $subject
            body: $body
            templateType: $templateType
        ) {
            subject
            body
        }
    }
`;

export default function ConfirmationEmailSetup({ eventId, role }) {
    const [subject, setSubject] = useState('');
    const [htmlBody, setHtmlBody] = useState('');
    const [richTextBody, setRichTextBody] = useState('');
    const [testEmail, setTestEmail] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isHtmlMode, setIsHtmlMode] = useState(true);

    const { data, loading, error, refetch } = useQuery(GET_CONFIRMATION_EMAIL_TEMPLATE, {
        variables: { eventId, role },
        errorPolicy: 'all', // Continue processing even if there are errors
        onError: (e) => {
            console.error('Error fetching confirmation email template:', e);
            setMessage({
                type: 'error',
                text: 'Failed to load confirmation email template. Please refresh the page.',
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [saveTemplate, { loading: saving }] = useMutation(SAVE_CONFIRMATION_EMAIL_TEMPLATE, {
        onCompleted: (data) => {
            setMessage({ type: 'success', text: data.saveConfirmationEmailTemplate });
            refetch();
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to save template' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [sendTestEmail, { loading: sendingTest }] = useMutation(SEND_TEST_CONFIRMATION_EMAIL, {
        onCompleted: (data) => {
            setMessage({ type: 'success', text: data.sendTestConfirmationEmail });
            setTestEmail('');
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to send test email' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [previewEmail, { loading: previewing }] = useMutation(PREVIEW_EMAIL_TEMPLATE, {
        onCompleted: (data) => {
            setPreviewData({
                subject: data.previewEmailTemplate.subject,
                body: data.previewEmailTemplate.body,
                isHtml: isHtmlMode,
            });
            setShowPreview(true);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to preview email' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    useEffect(() => {
        try {
            if (data?.confirmationEmailTemplate) {
                const template = data.confirmationEmailTemplate;
                setSubject(template.subject || '');
                if (template.isHtml) {
                    setHtmlBody(template.body || '');
                    setRichTextBody('');
                } else {
                    setRichTextBody(template.body || '');
                    setHtmlBody('');
                }
                setIsHtmlMode(template.isHtml !== false);
            }
        } catch (error) {
            console.error('Error processing confirmation email template data:', error);
            setMessage({
                type: 'error',
                text: 'Error processing template data. Please refresh the page.',
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
    }, [data]);

    const handleSave = async () => {
        try {
            const bodyToSave = isHtmlMode ? htmlBody : richTextBody;
            if (!bodyToSave.trim()) {
                setMessage({ type: 'error', text: 'Please enter email body content' });
                setTimeout(() => setMessage({ type: '', text: '' }), 5000);
                return;
            }

            await saveTemplate({
                variables: {
                    eventId,
                    role,
                    subject,
                    body: bodyToSave,
                    isHtmlMode,
                },
            });
        } catch (error) {
            console.error('Failed to save template:', error);
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) return;

        try {
            await sendTestEmail({
                variables: {
                    eventId,
                    role,
                    testEmail,
                },
            });
            setTestEmail('');
        } catch (error) {
            console.error('Failed to send test email:', error);
        }
    };

    const handlePreview = async () => {
        if (!subject.trim() || (!htmlBody.trim() && !richTextBody.trim())) {
            setMessage({ type: 'error', text: 'Please fill in both subject and body to preview' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        const body = isHtmlMode ? htmlBody : richTextBody;

        try {
            await previewEmail({
                variables: {
                    eventId,
                    role,
                    subject,
                    body,
                    templateType: 'interested',
                },
            });
        } catch (error) {
            console.error('Failed to preview email:', error);
        }
    };

    const availableVariables = data?.invitationVariables || [];

    if (loading) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <LinearProgress />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading confirmation email template...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Interested Email Setup
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure the email template that will be sent to users to express their
                            interest in the event. This email will contain an interested link that
                            users can click to show their interest.
                        </Typography>
                    </Box>

                    {message.text && (
                        <Alert
                            severity={message.type}
                            onClose={() => setMessage({ type: '', text: '' })}
                        >
                            {message.text}
                        </Alert>
                    )}

                    <Card>
                        <CardContent>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Available Variables
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mb: 2 }}
                                    >
                                        Use these variables in your email template. They will be
                                        automatically replaced with actual values when sending
                                        invitations.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {availableVariables.map((variable) => (
                                            <Chip
                                                key={variable}
                                                label={`$${variable}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                title={`User's ${variable} field`}
                                            />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Chip
                                            label="$interested_link"
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            title="Link for users to confirm their interest in the invitation"
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        <strong>$interested_link:</strong> This special link allows
                                        users to confirm or decline their interest in your
                                        invitation. When clicked, users will be taken to a
                                        confirmation page where they can express their interest.
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Email Subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Enter email subject..."
                                    helperText="Use variables like $Full Name, $Company, etc."
                                />

                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant="h6">Email Body</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2">Rich Text</Typography>
                                            <Switch
                                                checked={isHtmlMode}
                                                onChange={(e) => setIsHtmlMode(e.target.checked)}
                                            />
                                            <Typography variant="body2">HTML</Typography>
                                        </Box>
                                    </Box>

                                    {isHtmlMode ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={10}
                                            value={htmlBody}
                                            onChange={(e) => setHtmlBody(e.target.value)}
                                            placeholder="Enter HTML email body..."
                                            helperText="Use HTML tags for formatting. Variables will be replaced automatically."
                                        />
                                    ) : (
                                        <ReactQuill
                                            theme="snow"
                                            value={richTextBody}
                                            onChange={setRichTextBody}
                                            style={{ height: '200px', marginBottom: '50px' }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<IconDeviceFloppy />}
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Template'}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        startIcon={<IconEye />}
                                        onClick={handlePreview}
                                        disabled={previewing}
                                    >
                                        {previewing ? 'Generating Preview...' : 'Preview'}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        startIcon={<IconRefresh />}
                                        onClick={() => refetch()}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Test Email
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <TextField
                                    label="Test Email Address"
                                    type="email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="Enter email address to test..."
                                    sx={{ flexGrow: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<IconMail />}
                                    onClick={handleSendTest}
                                    disabled={!testEmail || sendingTest}
                                >
                                    {sendingTest ? 'Sending...' : 'Send Test'}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Box>

            {/* Preview Dialog */}
            {showPreview && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1300,
                    }}
                    onClick={() => setShowPreview(false)}
                >
                    <Paper
                        sx={{
                            maxWidth: '80%',
                            maxHeight: '80%',
                            overflow: 'auto',
                            p: 3,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h6">Email Preview</Typography>
                                <Button onClick={() => setShowPreview(false)}>Close</Button>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Subject:
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {previewData.subject}
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Body:
                                </Typography>
                                {previewData.isHtml ? (
                                    <Box
                                        dangerouslySetInnerHTML={{ __html: previewData.body }}
                                        sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            p: 2,
                                            borderRadius: 1,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {previewData.body}
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </Paper>
                </Box>
            )}
        </MainCard>
    );
}
